// "Ready to submit" — the checklist at the top of the claim editor.
//
// Every row comes from the server (`view.readiness`, built by
// claim_readiness_service.check), which is the same definition the submit queue
// refuses on. Nothing here decides readiness; it only shows the rows, so the
// editor and the queue can never disagree about what is wrong.
//
// Styling reuses the editor's existing section and status classes — no new
// colours, fonts or spacing (David has not set a design direction yet).

import React from "react";

export function readinessBlockReason(readiness) {
  if (!readiness || readiness.ready) return null;
  const red = (readiness.rows || []).filter((row) => !row.ok);
  return `Not ready to submit: ${red.map((row) => row.label).join("; ")}`;
}

// Where the coverage matrix lives for this practice. A plain link, not a page
// change: App.js mirrors `activePage` to this path and boots on it, and the
// checklist is mounted in a drawer that has no `onPageChange` to hand it.
export function coverageMatrixHref(practiceId) {
  return practiceId ? `/p/${practiceId}/pms-coverage` : null;
}

export default function ReadinessChecklist({ readiness, practiceId }) {
  if (!readiness) return null;
  const href = coverageMatrixHref(practiceId);
  const rows = readiness.rows || [];
  return (
    <section className="ce-section" aria-label="Ready to submit">
      <h2>
        Ready to submit{" "}
        <span
          className={`ce-status ${
            readiness.ready ? "ce-status-accepted" : "ce-status-denied"
          }`}
        >
          {readiness.ready ? "· all clear" : "· not yet"}
        </span>
      </h2>
      <ul className="ce-rules">
        {rows.map((row) => (
          <li
            key={row.key}
            data-ready-row={row.key}
            data-ok={row.ok ? "yes" : "no"}
            data-held-in={row.held_in || undefined}
          >
            {/* A row the practice's portal holds (Practice Fusion) is a note, not a
                check Atlas passed, so it gets no green tick. */}
            {row.held_in ? (
              <span className="ce-status">ℹ</span>
            ) : (
              <span
                className={`ce-status ${row.ok ? "ce-status-accepted" : "ce-status-denied"}`}
              >
                {row.ok ? "✓" : "✗"}
              </span>
            )}{" "}
            {row.label}
            {row.ok && row.detail && <span> — {row.detail}</span>}
            {/* Brief 15: under a "held in <portal>" note, the coverage matrix — only
                when the server says the portal's runbook can read the patient back
                (`coverage_matrix`, set when patient.demographics is covered). */}
            {row.held_in && row.coverage_matrix && href && (
              <span>
                {" "}
                ·{" "}
                <a href={href} data-testid="coverage-link">
                  What the portal runbook covers
                </a>
              </span>
            )}
            {(row.warnings || []).length > 0 && (
              <ul>
                {row.warnings.map((warning, i) => (
                  <li key={`w${i}`}>{warning}</li>
                ))}
              </ul>
            )}
            {!row.ok && row.problems.length > 0 && (
              <ul>
                {row.problems.map((problem, i) => (
                  <li key={i}>{problem}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
